import { useContext, useState, useCallback, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { ensureCsrfToken } from "../../../api/client";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  isUnverifiedStudentFirstOrderPreview,
  isVerifiedStudentUser,
} from "../../../utils/studentEligibility";
import { saveMenuScrollRestore } from "../../../utils/menuScrollRestore";
import { getGuestCart, mergeGuestCartToServer } from "../../../utils/guestCartStorage";
import { readMenuOrderType } from "../../../utils/menuOrderTypePreference";

const LS_LAST_CART = "leMemePrix_lastCartRestaurantId";
const STUDENT_DISCOUNTS_CAT_ID = "__student_discounts__";

const canUseStudentPricing = (user) =>
  Boolean(user && (isVerifiedStudentUser(user) || isUnverifiedStudentFirstOrderPreview(user)));

const StudentProfilePromptModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshAuth } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dob, setDob] = useState("");
  /** idle | question | confirmNotStudent | idConsent | code | welcome | offers */
  const [step, setStep] = useState("idle");
  const [offerSamples, setOfferSamples] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [restaurantOffers, setRestaurantOffers] = useState([]);
  const [restaurantOffersLoading, setRestaurantOffersLoading] = useState(false);
  const [welcomeOffers, setWelcomeOffers] = useState([]);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const afterWelcomeRef = useRef(null);

  const maxDobValue = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 13);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  })();

  const prevNeedsPromptRef = useRef(false);

  useEffect(() => {
    if (!user) {
      setStep("idle");
      prevNeedsPromptRef.current = false;
      return;
    }
    // Phone must be collected first (Google + email users).
    if (user.needsPhonePrompt) {
      setStep("idle");
      prevNeedsPromptRef.current = false;
      return;
    }
    if (user.needsStudentProfilePrompt && !prevNeedsPromptRef.current) {
      setStep("question");
    }
    prevNeedsPromptRef.current = !!user.needsStudentProfilePrompt;
  }, [user, user?.needsStudentProfilePrompt, user?.needsPhonePrompt]);

  const resolvePreferredRestaurantId = useCallback(() => {
    const pathMatch = String(location.pathname || "").match(/^\/restaurant\/([^/]+)/);
    if (pathMatch?.[1]) return String(pathMatch[1]);
    try {
      return localStorage.getItem(LS_LAST_CART) || "";
    } catch {
      return "";
    }
  }, [location.pathname]);

  // Menu items the user already selected for this restaurant — the guest cart
  // first, then the server cart (the guest cart is cleared once merged on
  // login). Used so the prompt surfaces that exact item with its student price.
  const collectSelectedMenuItemIds = useCallback(async (restaurantId) => {
    if (!restaurantId) return [];
    const idsFrom = (items) =>
      Array.from(
        new Set(
          (Array.isArray(items) ? items : [])
            .map((it) => String(it?.menuItemId || ""))
            .filter(Boolean),
        ),
      );
    const guestIds = idsFrom(getGuestCart(restaurantId)?.items);
    if (guestIds.length) return guestIds;
    try {
      const res = await api.get("/api/cart", { params: { restaurantId } });
      return idsFrom(res.data?.items);
    } catch {
      return [];
    }
  }, []);

  const loadOfferSamples = useCallback(async () => {
    setOffersLoading(true);
    setError("");
    try {
      const restaurantId = resolvePreferredRestaurantId();
      const selectedItemIds = await collectSelectedMenuItemIds(restaurantId);
      const res = await api.get("/api/auth/user/student-offer-samples", {
        params: {
          ...(restaurantId ? { restaurantId } : {}),
          ...(selectedItemIds.length ? { selectedItemIds: selectedItemIds.join(",") } : {}),
          limit: 8,
        },
      });
      setOfferSamples(Array.isArray(res.data?.data?.items) ? res.data.data.items : []);
    } catch {
      setOfferSamples([]);
      setError(t("studentOffersPrompt.loadError"));
    } finally {
      setOffersLoading(false);
    }
  }, [resolvePreferredRestaurantId, collectSelectedMenuItemIds, t]);

  // Restaurant offers (BOGO / discount / first_order_reward / bundle) for the
  // relevant restaurant — shown to every new account, not only students.
  const loadRestaurantOffers = useCallback(async () => {
    const restaurantId = resolvePreferredRestaurantId();
    if (!restaurantId) {
      setRestaurantOffers([]);
      return;
    }
    setRestaurantOffersLoading(true);
    try {
      const res = await api.get(`/api/restaurant-offers/restaurant/${restaurantId}`);
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRestaurantOffers(data);
    } catch {
      setRestaurantOffers([]);
    } finally {
      setRestaurantOffersLoading(false);
    }
  }, [resolvePreferredRestaurantId]);

  // Load every offer we can surface in the prompt: the restaurant's public
  // offers for all accounts, plus the student offers when eligible.
  const loadOffersForPrompt = useCallback(() => {
    loadRestaurantOffers();
    if (canUseStudentPricing(user)) {
      loadOfferSamples();
    } else {
      setOfferSamples([]);
    }
  }, [loadRestaurantOffers, loadOfferSamples, user]);

  const goToOffersStep = useCallback(() => {
    setStep("offers");
    loadOffersForPrompt();
  }, [loadOffersForPrompt]);

  const showConfirmNotStudent = Boolean(
    user?.needsStudentProfilePrompt && step === "confirmNotStudent"
  );
  const showIdConsent = Boolean(user?.needsStudentProfilePrompt && step === "idConsent");
  const showCode = Boolean(user && step === "code");
  const showWelcome = Boolean(user && step === "welcome");
  const showOffers = Boolean(user && step === "offers");
  const open =
    showCode ||
    showWelcome ||
    showOffers ||
    Boolean(
      user?.needsStudentProfilePrompt &&
        (step === "question" || step === "confirmNotStudent" || step === "idConsent")
    );

  const dismissCodeStep = useCallback(() => {
    goToOffersStep();
  }, [goToOffersStep]);

  const runAfterWelcome = useCallback(() => {
    const next = afterWelcomeRef.current;
    afterWelcomeRef.current = null;
    setWelcomeOffers([]);
    if (typeof next === "function") next();
    else setStep("idle");
  }, []);

  const dismissOffersStep = useCallback(() => {
    // After "see offers" from welcome, keep the deferred next step (e.g. student code).
    if (afterWelcomeRef.current) runAfterWelcome();
    else setStep("idle");
  }, [runAfterWelcome]);

  // Show the new-account welcome offer (if any) before running the next step.
  const goToWelcomeOrNext = useCallback(
    async (nextFn) => {
      afterWelcomeRef.current = typeof nextFn === "function" ? nextFn : () => setStep("idle");
      setWelcomeLoading(true);
      setWelcomeOffers([]);
      setStep("welcome");
      // Prefetch the relevant offers so the welcome gift can surface a "see
      // offers" CTA — restaurant offers for everyone, student offers when
      // eligible — but only when some are actually available now.
      loadOffersForPrompt();
      try {
        const res = await api.get("/api/offers/welcome");
        const offers = Array.isArray(res.data?.data) ? res.data.data : [];
        if (offers.length === 0) {
          runAfterWelcome();
          return;
        }
        setWelcomeOffers(offers);
      } catch {
        runAfterWelcome();
      } finally {
        setWelcomeLoading(false);
      }
    },
    [runAfterWelcome, loadOffersForPrompt]
  );

  const onWelcomeContinue = useCallback(() => {
    runAfterWelcome();
  }, [runAfterWelcome]);

  // Jump into the offers list without dropping the deferred next step (student code).
  const onWelcomeSeeOffers = useCallback(() => {
    setWelcomeOffers([]);
    setStep("offers");
  }, []);

  // An item picked while still a guest is stored at the regular price (the
  // declaration happens after the guest cart is merged to the server). Once the
  // user becomes a student, reprice the cart so eligible lines pick up the
  // student variant price instead of the regular one.
  const repriceCartForStudentPricing = useCallback(async () => {
    const restaurantId = resolvePreferredRestaurantId();
    if (!restaurantId) return;
    try {
      // Ensure the guest cart is on the server first (no-op once merged/empty).
      await mergeGuestCartToServer(api, restaurantId);
      await api.post("/api/cart/reprice", {
        restaurantId,
        orderType: readMenuOrderType(restaurantId),
      });
    } catch {
      /* pricing is re-confirmed server-side at payment; ignore here */
    }
  }, [resolvePreferredRestaurantId]);

  const submitDeclaration = useCallback(
    async (isStudent, showStudentIdAtRestaurant, dateOfBirth) => {
      if (!user?.id || busy) return false;
      setError("");
      setBusy(true);
      try {
        await ensureCsrfToken();
        const decl = { isStudent };
        if (isStudent) {
          decl.showStudentIdAtRestaurant = showStudentIdAtRestaurant === true;
          if (dateOfBirth) decl.dateOfBirth = dateOfBirth;
        }
        const res = await api.patch("/api/auth/user/me", {
          studentProfileDeclaration: decl,
        });
        if (res.data?.ok) {
          await refreshAuth();
          if (isStudent) await repriceCartForStudentPricing();
          return true;
        }
        return false;
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || t("studentProfilePrompt.submitError");
        setError(msg);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [user?.id, busy, refreshAuth, repriceCartForStudentPricing, t]
  );

  const onChoseNotStudent = useCallback(() => {
    setStep("confirmNotStudent");
  }, []);

  const onConfirmNotStudent = useCallback(async () => {
    const ok = await submitDeclaration(false);
    if (ok) goToWelcomeOrNext(() => setStep("idle"));
  }, [submitDeclaration, goToWelcomeOrNext]);

  const onBackToQuestion = useCallback(() => {
    setError("");
    setStep("question");
  }, []);

  const onChoseStudent = useCallback(() => {
    setStep("idConsent");
  }, []);

  const onAcceptShowId = useCallback(async () => {
    if (!dob) {
      setError(t("studentProfilePrompt.dobRequired"));
      return;
    }
    const ok = await submitDeclaration(true, true, dob);
    if (ok) goToWelcomeOrNext(() => setStep("code"));
  }, [submitDeclaration, dob, t, goToWelcomeOrNext]);

  const onDeclineShowId = useCallback(async () => {
    const ok = await submitDeclaration(true, false, dob || undefined);
    if (ok) goToWelcomeOrNext(() => setStep("idle"));
  }, [submitDeclaration, dob, goToWelcomeOrNext]);

  // Does this restaurant's cart already hold the given menu item? Checks the
  // guest cart first, then the server cart for signed-in users.
  const cartHasMenuItem = useCallback(async (restaurantId, menuItemId) => {
    if (!restaurantId || !menuItemId) return false;
    const wanted = String(menuItemId);
    const guestCart = getGuestCart(restaurantId);
    if (Array.isArray(guestCart?.items) && guestCart.items.length > 0) {
      return guestCart.items.some((it) => String(it?.menuItemId || "") === wanted);
    }
    try {
      const res = await api.get("/api/cart", { params: { restaurantId } });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      return items.some((it) => String(it?.menuItemId || "") === wanted);
    } catch {
      return false;
    }
  }, []);

  // Student offers are informational here. If the user already configured this
  // exact item in the cart, jump straight to the cart/checkout so their
  // selections are kept. Otherwise open the restaurant's student category so
  // they can configure that item.
  const onSelectOffer = useCallback(
    async (offer) => {
      const restaurantId = offer?.restaurantId ? String(offer.restaurantId) : "";
      if (!restaurantId) return;
      const offerItemId = offer?.menuItemId ? String(offer.menuItemId) : "";
      try {
        localStorage.setItem(LS_LAST_CART, restaurantId);
      } catch {
        /* ignore */
      }
      const alreadyInCart = await cartHasMenuItem(restaurantId, offerItemId);
      if (alreadyInCart) {
        setStep("idle");
        navigate(`/cart/${restaurantId}`, {
          state: { autoProceedToCheckout: true },
        });
        return;
      }
      saveMenuScrollRestore(restaurantId, {
        categoryId: STUDENT_DISCOUNTS_CAT_ID,
        scrollTop: 0,
        menuItemId: offerItemId,
      });
      setStep("idle");
      navigate(`/restaurant/${restaurantId}`);
    },
    [navigate, cartHasMenuItem]
  );

  const onSelectRestaurantOffer = useCallback(
    (offer) => {
      const restaurantId = offer?.restaurantId ? String(offer.restaurantId) : "";
      if (!restaurantId) return;
      try {
        localStorage.setItem(LS_LAST_CART, restaurantId);
      } catch {
        /* ignore */
      }
      setStep("idle");
      navigate(`/restaurant/${restaurantId}`);
    },
    [navigate]
  );

  const describeRestaurantOffer = useCallback(
    (offer) => {
      if (offer?.offerType === "discount" && offer.discount) {
        const r = offer.discount;
        const val =
          r.discountType === "percent"
            ? `${r.discountValue}%`
            : `€${(Number(r.discountValue || 0) / 100).toFixed(2)}`;
        return t("restaurantMenu.discountLabel", { value: val });
      }
      if (offer?.offerType === "bogo" && offer.bogo) {
        return t("restaurantMenu.buyXGetYLabel", {
          buy: offer.bogo.buyQuantity,
          free: offer.bogo.freeQuantity,
        });
      }
      if (offer?.offerType === "buy_x_get_y" && offer.buyXGetY) {
        return t("restaurantMenu.buyXGetYLabel", {
          buy: offer.buyXGetY.buyQuantity,
          free: offer.buyXGetY.freeQuantity,
        });
      }
      if (offer?.offerType === "first_order_reward" && offer.firstOrderReward) {
        return t("restaurantMenu.firstOrderRewardLabel", {
          min: Number(offer.firstOrderReward.minOrderEuro || 0).toFixed(0),
          qty: offer.firstOrderReward.freeQuantity || 1,
        });
      }
      if (offer?.offerType === "bundle" && offer.bundle) {
        return `€${Number(offer.bundle.bundlePrice || 0).toFixed(2)}`;
      }
      return "";
    },
    [t]
  );

  if (!open || !user) return null;

  if (showOffers) {
    const titleId = "student-offers-prompt-title";
    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
          <div className="text-center mb-5">
            <span className="text-4xl mb-3 block" aria-hidden="true">
              🎁
            </span>
            <h2 id={titleId} className="text-xl font-bold text-gray-900 mb-2">
              {t("welcomeOffer.offersTitle")}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed">{t("welcomeOffer.offersBody")}</p>
          </div>

          {restaurantOffersLoading || offersLoading ? (
            <p className="text-center text-sm text-gray-500 py-8">{t("studentOffersPrompt.loading")}</p>
          ) : restaurantOffers.length === 0 && offerSamples.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-6">{t("welcomeOffer.offersEmpty")}</p>
          ) : (
            <>
              {restaurantOffers.length > 0 ? (
                <div className="mb-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    {t("welcomeOffer.allOffersTitle")}
                  </h3>
                  <ul className="space-y-2">
                    {restaurantOffers.map((offer) => {
                      const label = describeRestaurantOffer(offer);
                      return (
                        <li key={offer._id}>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onSelectRestaurantOffer(offer)}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-brand/20 bg-brand/5 hover:bg-brand/10 hover:border-brand/30 p-3 text-left transition-colors disabled:opacity-60"
                          >
                            {offer.image ? (
                              <img
                                src={offer.image}
                                alt=""
                                className="h-14 w-14 rounded-lg object-cover shrink-0 bg-gray-100"
                              />
                            ) : (
                              <div
                                className="h-14 w-14 rounded-lg bg-brand/15 shrink-0 flex items-center justify-center text-xl"
                                aria-hidden="true"
                              >
                                🏷️
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 truncate">{offer.name}</div>
                              {offer.description ? (
                                <div className="text-xs text-gray-500 truncate">
                                  {offer.description}
                                </div>
                              ) : null}
                              {label ? (
                                <div className="mt-1 text-sm font-bold text-brand">{label}</div>
                              ) : null}
                            </div>
                            <span className="text-brand font-bold shrink-0" aria-hidden="true">
                              →
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              {offerSamples.length > 0 ? (
                <div className="mb-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    {t("welcomeOffer.studentOffersTitle")}
                  </h3>
                  <ul className="space-y-2">
                    {offerSamples.map((offer) => {
                      const savings =
                        offer.regularPrice > offer.studentPrice
                          ? (offer.regularPrice - offer.studentPrice).toFixed(2)
                          : null;
                      return (
                        <li key={`${offer.restaurantId}-${offer.menuItemId}`}>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onSelectOffer(offer)}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-300 p-3 text-left transition-colors disabled:opacity-60"
                          >
                            {offer.image ? (
                              <img
                                src={offer.image}
                                alt=""
                                className="h-14 w-14 rounded-lg object-cover shrink-0 bg-gray-100"
                              />
                            ) : (
                              <div
                                className="h-14 w-14 rounded-lg bg-emerald-100 shrink-0 flex items-center justify-center text-xl"
                                aria-hidden="true"
                              >
                                🍽️
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="font-semibold text-gray-900 truncate">{offer.itemName}</div>
                                {offer.isSelected ? (
                                  <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                    {t("studentOffersPrompt.selectedBadge")}
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{offer.restaurantName}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                                <span className="font-bold text-emerald-700">
                                  €{Number(offer.studentPrice).toFixed(2)}
                                </span>
                                {offer.regularPrice > offer.studentPrice ? (
                                  <span className="text-gray-400 line-through text-xs">
                                    €{Number(offer.regularPrice).toFixed(2)}
                                  </span>
                                ) : null}
                                {savings ? (
                                  <span className="text-xs font-semibold text-emerald-600">
                                    {t("studentOffersPrompt.saveBadge", { value: savings })}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <span className="text-emerald-600 font-bold shrink-0" aria-hidden="true">
                              →
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </>
          )}

          {error ? (
            <p className="mb-4 text-sm text-red-600 text-center" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="button"
            disabled={busy}
            onClick={dismissOffersStep}
            className="w-full py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 transition-colors"
          >
            {t("studentOffersPrompt.skip")}
          </button>
        </div>
      </div>
    );
  }

  if (showWelcome) {
    const titleId = "welcome-offer-prompt-title";
    const bestCents = welcomeOffers.reduce(
      (max, o) => (Number(o.discountCents) || 0) > max ? Number(o.discountCents) || 0 : max,
      0
    );
    const discountLabel = `€${(bestCents / 100).toFixed(2)}`;
    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 sm:p-8">
          {welcomeLoading ? (
            <p className="text-center text-sm text-gray-500 py-10">
              {t("welcomeOffer.loading")}
            </p>
          ) : (
            <>
              <div className="text-center mb-6">
                <span className="text-4xl mb-3 block" aria-hidden="true">
                  🎁
                </span>
                <h2 id={titleId} className="text-xl font-bold text-gray-900 mb-2">
                  {t("welcomeOffer.title")}
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">{t("welcomeOffer.body")}</p>
              </div>
              <div className="mb-6 rounded-xl border-2 border-brand/30 bg-brand/5 px-4 py-5 text-center">
                <div className="text-2xl font-extrabold text-brand">
                  {t("welcomeOffer.discountLine", { value: discountLabel })}
                </div>
                {welcomeOffers[0]?.name ? (
                  <div className="mt-1 text-xs font-medium text-gray-500">
                    {welcomeOffers[0].name}
                  </div>
                ) : null}
              </div>
              {restaurantOffers.length > 0 || offerSamples.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={onWelcomeSeeOffers}
                    className="w-full py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold transition-colors"
                  >
                    {t("welcomeOffer.seeOffers")}
                  </button>
                  <button
                    type="button"
                    onClick={onWelcomeContinue}
                    className="w-full py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    {t("welcomeOffer.continue")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onWelcomeContinue}
                  className="w-full py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold transition-colors"
                >
                  {t("welcomeOffer.continue")}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (showCode) {
    const code = user.studentRefCode || "";
    const titleId = "student-profile-code-step-title";
    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 sm:p-8">
          <div className="text-center mb-6">
            <span className="text-4xl mb-4 block" aria-hidden="true">
              🎓
            </span>
            <h2 id={titleId} className="text-xl font-bold text-gray-900 mb-2">
              {t("studentProfilePrompt.codeStepTitle")}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed text-left sm:text-center">
              {t("studentProfilePrompt.codeStepBody")}
            </p>
          </div>
          <div className="mb-6 rounded-xl border-2 border-brand/30 bg-brand/5 px-4 py-4 text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              {t("student.studentCode")}
            </div>
            <div
              className="font-mono text-xl sm:text-2xl font-bold text-gray-900 tracking-[0.2em] break-all select-all"
              translate="no"
            >
              {code || "—"}
            </div>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed text-center mb-6">
            <Trans
              i18nKey="studentProfilePrompt.codeStepSettingsHint"
              components={{
                settingsLink: (
                  <Link
                    to="/settings"
                    className="font-semibold text-brand hover:underline underline-offset-2"
                    onClick={dismissCodeStep}
                  />
                ),
              }}
            />
          </p>
          <button
            type="button"
            onClick={dismissCodeStep}
            className="w-full py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold transition-colors"
          >
            {t("studentProfilePrompt.codeStepContinue")}
          </button>
        </div>
      </div>
    );
  }

  if (showConfirmNotStudent) {
    const titleId = "student-profile-confirm-not-student-title";
    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 sm:p-8">
          <button
            type="button"
            disabled={busy}
            onClick={onBackToQuestion}
            className="mb-2 inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-60 transition-colors"
            aria-label={t("studentProfilePrompt.back")}
          >
            <span aria-hidden="true">←</span> {t("studentProfilePrompt.back")}
          </button>
          <div className="text-center mb-6">
            <span className="text-4xl mb-4 block" aria-hidden="true">
              🙋
            </span>
            <h2 id={titleId} className="text-xl font-bold text-gray-900 mb-2">
              {t("studentProfilePrompt.confirmNotStudentTitle")}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              {t("studentProfilePrompt.confirmNotStudentBody")}
            </p>
          </div>
          {error ? (
            <p className="mb-4 text-sm text-red-600 text-center" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={onConfirmNotStudent}
              className="flex-1 py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold transition-colors"
            >
              {t("studentProfilePrompt.confirmNotStudentYes")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onBackToQuestion}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 transition-colors"
            >
              {t("studentProfilePrompt.goBack")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showIdConsent) {
    const titleId = "student-profile-id-consent-title";
    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 sm:p-8">
          <button
            type="button"
            disabled={busy}
            onClick={onBackToQuestion}
            className="mb-2 inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-60 transition-colors"
            aria-label={t("studentProfilePrompt.back")}
          >
            <span aria-hidden="true">←</span> {t("studentProfilePrompt.back")}
          </button>
          <div className="text-center mb-6">
            <span className="text-4xl mb-4 block" aria-hidden="true">
              🪪
            </span>
            <h2 id={titleId} className="text-xl font-bold text-gray-900 mb-2">
              {t("studentProfilePrompt.notActivatedTitle")}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed text-left">
              {t("studentProfilePrompt.notActivatedBody")}
            </p>
          </div>
          <div className="mb-5">
            <label
              htmlFor="student-dob"
              className="block text-sm font-semibold text-gray-700 mb-1"
            >
              {t("studentProfilePrompt.dobLabel")}
            </label>
            <input
              id="student-dob"
              type="date"
              value={dob}
              max={maxDobValue}
              onChange={(e) => {
                setDob(e.target.value);
                setError("");
              }}
              className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 focus:border-brand focus:outline-none"
            />
            <p className="mt-1 text-xs text-gray-500 leading-relaxed">
              {t("studentProfilePrompt.dobHint")}
            </p>
          </div>
          {error ? (
            <p className="mb-4 text-sm text-red-600 text-center" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={onAcceptShowId}
              className="flex-1 py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold transition-colors"
            >
              {t("studentProfilePrompt.agreeShowId")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onDeclineShowId}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 transition-colors"
            >
              {t("studentProfilePrompt.declineShowId")}
            </button>
          </div>
          <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
            {t("studentProfilePrompt.declineHint")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="student-profile-prompt-title"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <span className="text-4xl mb-4 block" aria-hidden="true">
            🎓
          </span>
          <h2 id="student-profile-prompt-title" className="text-xl font-bold text-gray-900 mb-2">
            {t("studentProfilePrompt.title")}
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed">{t("studentProfilePrompt.body")}</p>
        </div>
        {error ? (
          <p className="mb-4 text-sm text-red-600 text-center" role="alert">
            {error}
          </p>
        ) : null}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onChoseStudent}
            className="flex-1 py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold transition-colors"
          >
            {t("studentProfilePrompt.yesStudent")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onChoseNotStudent}
            className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 transition-colors"
          >
            {t("studentProfilePrompt.notStudent")}
          </button>
        </div>
        <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
          {t("studentProfilePrompt.hint")}
        </p>
      </div>
    </div>
  );
};

export default StudentProfilePromptModal;
